import { useState, useEffect, useRef } from "react";
import { FaTimes, FaBars } from "react-icons/fa";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import brandLogo from "../assets/brandLogo.png"

gsap.registerPlugin(ScrollTrigger);

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navRef = useRef<HTMLDivElement>(null);
  const linksRef = useRef<(HTMLAnchorElement | null)[]>([]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const getBrandLogo = () => {
    return brandLogo;
  };

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Entrance Animation
      gsap.from(navRef.current, {
        y: -100,
        opacity: 100,
        duration: 1.5,
        ease: "power4.out",
      });

      /* 
      gsap.from(linksRef.current.filter(Boolean), {
        y: -20,
        opacity: 0,
        duration: 0.8,
        stagger: 0.1,
        ease: "power3.out",
        delay: 0.6,
        clearProps: "all",
      });
      */

      // Liquid Scroll Transition (Morphing effect)
      if (navRef.current) {
        gsap.to(navRef.current, {
          scrollTrigger: {
            trigger: "body",
            start: "top -20",
            end: "top -100",
            scrub: 1,
          },
          paddingTop: "8px",
          paddingBottom: "8px",
          ease: "none",
        });
      }
    }, navRef);

    return () => ctx.revert();
  }, []);

  return (
    <div
      ref={navRef}
      className={`fixed top-0 left-0 w-full z-[1000] transition-all duration-700 ease-in-out ${scrolled ? 'py-0' : 'py-0'}`}
    >
      <nav
        className={`w-full flex items-center justify-between transition-all duration-700 relative border-b border-text-primary/10 ${scrolled
          ? 'bg-white/95 backdrop-blur-md px-8 md:px-12 py-4 shadow-[0_10px_30px_rgba(0,0,0,0.05)]'
          : 'bg-white backdrop-blur-sm px-8 md:px-12 py-6 text-white'
          }`}
      >
        {/* Logo */}
        <a href="/" className="flex items-center group relative z-10">
          <img
            className="h-10 md:h-14 object-contain transition-all duration-500 group-hover:scale-105"
            src={getBrandLogo()}
            alt={import.meta.env.VITE_COMPANY_NAME}
          />
        </a>

        {/* Desktop Menu */}
        <div className="hidden md:flex items-center gap-8 relative z-10 ">
          {[
            { label: "Home", href: "/" },
            { label: "About Us", href: "/about" },
            { label: "Services", href: "/#services" },
            { label: "Loan Calculator", href: "/loan-calculator" },
            { label: "Contact Us", href: "/contact" },
            { label: "Repay", href: "/repay" },
          ].map((item, i) => (
            <a
              key={item.label}
              ref={(el) => { linksRef.current[i] = el; }}
              href={item.href}
              className="relative text-text-primary font-black text-[11px] uppercase tracking-[0.2em] transition-all hover:text-secondary group"
            >
              {item.label}
              <span className="absolute left-0 -bottom-1 w-0 h-[2px] bg-secondary transition-all duration-500 ease-out group-hover:w-full opacity-0 group-hover:opacity-100" />
            </a>
          ))}

          <a
            href={import.meta.env.VITE_WEBSITE_LINK}
            target="_blank"
            className="relative ml-2 px-8 py-3.5 border-2 border-primary text-primary font-bold text-[11px] uppercase tracking-[0.2em] transition-all hover:bg-primary hover:text-white rounded-full"
          >
            Sign In
          </a>

          {/* Premium Sharp CTA */}
          <a
            href={import.meta.env.VITE_WEBSITE_LINK}
            target="_blank"
            className="relative ml-2 px-10 py-4 bg-primary text-white font-bold text-[11px] uppercase tracking-[0.2em] transition-all hover:bg-secondary shadow-md hover:shadow-lg rounded-full"
          >
            Apply Loan
          </a>
        </div>

        {/* Mobile Toggle */}
        <button
          className="md:hidden w-11 h-11 bg-primary rounded-xl flex items-center justify-center text-white transition-all hover:bg-secondary active:scale-90"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          {menuOpen ? <FaTimes size={16} /> : <FaBars size={16} />}
        </button>

      </nav>

      {/* Mobile Dropdown - outside nav, relative to fixed wrapper */}
      <div
        className={`w-full transition-all duration-300 origin-top overflow-hidden z-[999] ${menuOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0 pointer-events-none'}`}
      >
        <div className="bg-white shadow-xl border-t border-gray-100 px-6 py-4 flex flex-col gap-2">
          {[
            { label: "Home", href: "/" },
            { label: "About Us", href: "/about" },
            { label: "Services", href: "/#services" },
            { label: "Loan Calculator", href: "/loan-calculator" },
            { label: "Contact Us", href: "/contact" },
            { label: "Repay Now", href: "/repay" },
          ].map((item) => (
            <a
              key={item.label}
              href={item.href}
              className="flex items-center justify-between px-5 py-4 rounded-2xl hover:bg-blue-50 text-text-primary hover:text-primary transition-all duration-200"
              onClick={() => setMenuOpen(false)}
            >
              <span className="font-semibold text-sm">{item.label}</span>
              <span className="text-primary text-xs">›</span>
            </a>
          ))}
          <div className="flex flex-col gap-3 pt-4 border-t border-gray-100 mt-2">
            <a href={import.meta.env.VITE_WEBSITE_LINK} target="_blank" className="w-full h-12 flex items-center justify-center border-2 border-primary text-primary font-bold text-sm rounded-2xl hover:bg-primary hover:text-white transition-all">
              Sign In
            </a>
            <a href={import.meta.env.VITE_WEBSITE_LINK} target="_blank" className="w-full h-12 flex items-center justify-center bg-primary text-white font-bold text-sm rounded-2xl hover:bg-secondary transition-all shadow-md">
              Apply Loan
            </a>
          </div>
        </div>
      </div>

      <style dangerouslySetInnerHTML={{
        __html: `
        @keyframes slow-slide {
          from { transform: translateX(-20%); }
          to { transform: translateX(20%); }
        }
        .animate-slow-slide {
          animation: slow-slide 10s linear infinite alternate;
        }
      `}} />
    </div>
  );
};

export default Navbar;
